import React, { Fragment, useEffect, useState, useRef } from "react";
import { Container } from "react-bootstrap";
import { Helmet } from 'react-helmet-async';
import { useLocation, useParams } from "react-router-dom";
import HeroImage from './HeroImage/HeroImage';
import ProductSpecifications from './ProductSpecifications/ProductSpecifications';
import PurchasingProcess from './PurchasingProcess/PurchasingProcess';
import Articles from './Articles/Articles';
import TermArticles from "./Articles/TermArticle";
import FAQs from './FAQs/FAQs';
import BeforeFooter from '../BeforeFooter/BeforeFooter';
import Marketing from './Marketing/Marketing';
import PermData from '../Helpers/Data/Products/Perm/PermData';
import FinalExpenseData from "../Helpers/Data/Products/FinalExpense/FinalExpenseData";
import TermData from '../Helpers/Data/Products/Term/TermData';
import TermInsuranceData from "../Helpers/Data/Products/TermInsurance/TermInsuranceData";
import DigitalTermData from '../Helpers/Data/Products/Term/DigitalTermData';
import EtradeTermData from '../Helpers/Data/Products/EtradeTerm/EtradeTermData';
import JuveData from '../Helpers/Data/Products/Juve/JuveData';
import LTCData from '../Helpers/Data/Products/LTC/LTCData';
import DisabilityData from '../Helpers/Data/Products/Disability/DisabilityData';
import NaviData from '../Helpers/Data/Products/Navi/NaviData';
import SuppData from '../Helpers/Data/Products/Supplemental/SuppData';
import HospitalData from '../Helpers/Data/Products/Hospital/HospitalConfinment';
import LongTermDisabilityData from "../Helpers/Data/Products/LongTermDisability/LongTermDisabilityData";
import Reviews from './Reviews/Reviews';
import { getSessionStorageItem, SESSION_STORAGE_ACCOUNT_KEY } from '../../sessionStorage';
import TermAbout from "../LandingPages/About/TermAbout";
import FinalExpenseAbout from "../LandingPages/About/FinalExpenseAbout";
import WholeLifeAbout from "../LandingPages/About/WholeLifeAbout";
import UniversalLifeAbout from "../LandingPages/About/UniversalLifeAbout";
import BusinessSolutionsAbout from "../LandingPages/About/BusinessSolutionsAbout";
import Carriers from "../Home/Carriers/Carriers";
import ProductPolicyCost from "./ProductPolicyCost/ProductPolicyCost";
import TermPurchasing from "./PurchasingProcess/TermPurchasingProcess";
import FinalExpensePurchasing from "./PurchasingProcess/FinalExpensePurchasingProcess";
import WholeLifePurchasing from "./PurchasingProcess/WholeLifePurchasingProcess";
import WholeLifeData from "../Helpers/Data/Products/WholeLife/WholeLifeData";
import UniversalLifePurchasing from "./PurchasingProcess/UniversalLifePurchasingProcess";
import LoanProtectionData from "../Helpers/Data/Products/LoanProtection/LoanProtectionData";
import LoanProtectionPurchasing from "./PurchasingProcess/LoanProtectionPurchasingProcess";
import LoanProtectionAbout from "../LandingPages/About/LoanProtectionAbout";
import GetProductDetail from "../Common/GetProductDetail";
import UniversalLifeData from "../Helpers/Data/Products/UniversalLife/UniversalLifeData";
import BusinessSolutionsData from "../Helpers/Data/Products/BusinessSolutions/BusinessSolutionsData";
import BusinessSolutionsPurchasing from "./PurchasingProcess/BusinessSolutionsPurchasingProcess";
import Products from "./Products/Products";
import SolutionArticleSummary from "./SolutionsArticleSummary/SolutionArticleSummary";
import SolutionArticleDetail from "./SolutionsArticleDetail/SolutionArticleDetail";
import Statistics from "./Statistics/Statistics";
import TermVersionControl from "./TermVersion/TermVersionControl";
import './ProductLandingPage.scss';

export default function ProductLandingPage() {

  const [heroImageUrl, setHeroImageUrl] = useState(null);
  const [heroImageTabletUrl, setHeroImageTabletUrl] = useState(null);
  const [heroImageMobileUrl, setHeroImageMobileUrl] = useState(null);
  const [isPermProductAvailable, setIsPermProductAvailable] = useState(false);
  const [isAccountLandingOverride, setIsAccountLandingOverride] = useState(null);
  const account = getSessionStorageItem(SESSION_STORAGE_ACCOUNT_KEY);
  const isTestimonialPanelEnable = account && account.account && account.account.isTestimonialPanelEnable;
  const aboutMeFormRef = useRef(null);
  const location = useLocation();
  const isFromSimplifiedIssueTerm = location.state && location.state.isFromSimplifiedIssueTerm ? true : false;

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  };

  const scrollToElement = (anchor, offSet) => {
    if (anchor) {
      const element = document.getElementById(anchor);
      const y = !offSet ? 
        element.getBoundingClientRect().top + window.scrollY : 
        element.getBoundingClientRect().top - offSet + window.scrollY;
      window.scrollTo({top: y, behavior: "smooth"});
    }
  };

  // Function to trigger the form submission
  const handleExternalSubmit = (noScroll) => {
    if (aboutMeFormRef.current) {
      // Dispatch a submit event on the form element
      if (aboutMeFormRef.current.dispatchSubmit) {
        aboutMeFormRef.current.dispatchSubmit();
      } else {
        aboutMeFormRef.current.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      }
    }
    if (noScroll === true) return;
    scrollToTop();
  };

  let data;
  let isTermVersion;
  let productDetail;
  let { product } = useParams();

  //workaround for Haven's wrong landing url
  const url = window.location.href;
  if (url.includes("digital-term")) {
    product = "digital-term";
  };

  const isTermInsurance = product === 'term-insurance';
  const isFinalExpense = product === 'final-expense';
  const isWholeLife = product === 'whole-life';
  const isUniversalLife = product === 'universal-life';
  const isLoanProtection = product === 'loan-protection';
  const isBusinessSolutions = product === 'business-solutions';
  const isPermProduct = product === 'permanent';
  const isJuviProduct = product === 'children';
  const isSupplemental = product === 'supplemental';

  useEffect(() => {
    window.scrollTo(0, 0);
    const accountProductTypeSettings = account.accountProducts.find(product => isFromSimplifiedIssueTerm
      ? product.productLandingUrl === '/landing/simplified-issue-term'
      : product.productLandingUrl === location.pathname);
    const accountLandingOverride = account.accountOverrides && accountProductTypeSettings ? 
      account.accountOverrides.find(override => override.accountProductTypeId === accountProductTypeSettings.productTypeId) : null;
    
    setIsAccountLandingOverride(!!accountLandingOverride);
    
    setIsPermProductAvailable(account.accountProducts.some(product => product.productLandingUrl === "/landing/permanent"));

    setHeroImageUrl(
      accountLandingOverride &&
      (accountProductTypeSettings ? accountProductTypeSettings.productTypeId === accountLandingOverride.accountProductTypeId
        : null) && accountLandingOverride.landingHeroImageUrl ? accountLandingOverride.landingHeroImageUrl :
      (accountProductTypeSettings && accountProductTypeSettings.heroImageUrl) || `/product-landing/${product.substring(0, 4)}/hero-image.jpg`);
    setHeroImageTabletUrl(
      accountLandingOverride &&
      (accountProductTypeSettings ? accountProductTypeSettings.productTypeId === accountLandingOverride.accountProductTypeId
        : null) && accountLandingOverride.landingHeroImageTabletUrl ? accountLandingOverride.landingHeroImageTabletUrl :
      (accountProductTypeSettings && accountProductTypeSettings.heroImageTabletUrl) || `/product-landing/${product.substring(0, 4)}/hero-tab-image.jpg`);
    setHeroImageMobileUrl(
      accountLandingOverride &&
      (accountProductTypeSettings ? accountProductTypeSettings.productTypeId === accountLandingOverride.accountProductTypeId
        : null) && accountLandingOverride.landingHeroImageMobileUrl ? accountLandingOverride.landingHeroImageMobileUrl :
      (accountProductTypeSettings && accountProductTypeSettings.heroImageMobileUrl) || `/product-landing/${product.substring(0, 4)}/hero-small.jpg`);
  }, []);

  switch (product) {
    case "permanent":
      data = PermData({ aboutMeFormRef: aboutMeFormRef });
      break;
    case "whole-life": 
      data = WholeLifeData({ isWholeLife: isWholeLife, aboutMeFormRef: aboutMeFormRef });
      break;
    case "universal-life":
      data = UniversalLifeData({ isUniversalLife: isUniversalLife, aboutMeFormRef: aboutMeFormRef });
      break;
    case "term":
      productDetail = GetProductDetail(['/landing/term', '/quote/term-life-insurance/multi-carrier-term']);
      if (productDetail === undefined) {
        productDetail = GetProductDetail(['/landing/term-insurance', '/term-insurance']);
        const redirectPath = productDetail === undefined ? '/' : '/landing/term-insurance';
        window.location = redirectPath;
      }
      else {
        data = TermData();
      }
      break;
    case "term-insurance":
      productDetail = GetProductDetail(isFromSimplifiedIssueTerm ? ['/landing/simplified-issue-term'] : ['/landing/term-insurance', '/term-insurance']);
      isTermVersion = productDetail?.landingScreenVersion !== null;
      if (productDetail === undefined) {
        window.location = '/';
      }
      data = productDetail.landingScreenVersion ? 
        TermVersionControl(
        { 
          version: productDetail.landingScreenVersion,
          content: productDetail.landingScreenConfiguration,
          aboutMeFormRef: aboutMeFormRef,
          isPermProductAvailable: isPermProductAvailable,
          isAccountLandingOverride: isAccountLandingOverride,
          isSimplifiedIssueTerm: isFromSimplifiedIssueTerm,
          action: () => handleExternalSubmit(true),
          scrollToElement: (anchor, offSet) => scrollToElement(anchor, offSet),
          heroImages: {
            desktop: heroImageUrl,
            tablet: heroImageUrl,
            mobile: heroImageUrl,
          } 
        }) : TermInsuranceData({ isTermInsurance: isTermInsurance, aboutMeFormRef: aboutMeFormRef, isSimplifiedIssueTerm: isFromSimplifiedIssueTerm });
      break;
    case "final-expense":
      data = FinalExpenseData({ isFinalExpense: isFinalExpense, aboutMeFormRef: aboutMeFormRef });
      break;
    case "business-solutions":
      data = BusinessSolutionsData({ isBusinessSolutions: isBusinessSolutions });
      break;
    case "children":
      data = JuveData({ aboutMeFormRef: aboutMeFormRef });
      break;
    case "long-term-care":
      data = LTCData();
      break;
    case "loan-protection":
      data = LoanProtectionData({ isLoanProtection: isLoanProtection, aboutMeFormRef: aboutMeFormRef });
      break;
    case "disability-income":
      data = DisabilityData();
      break;
    case "navigating-medicare":
      data = NaviData();
      break;
    case "supplemental":
      data = SuppData({ aboutMeFormRef: aboutMeFormRef, handleSubmitClick: handleExternalSubmit });
      break;
    case "hospital":
      data = HospitalData();
      break;
    case "term-s":
    case "etrade-term":
      data = EtradeTermData();
      break;
    case "digital-term":
      productDetail = GetProductDetail(['/landing/digital-term', '/landing/term/digital-term' ]);
      if (productDetail === undefined) {
        productDetail = GetProductDetail(['/landing/term-insurance', '/term-insurance']);
        const redirectPath = productDetail === undefined ? '/' : '/landing/term-insurance';
        window.location = redirectPath;
      }
      else {
        data = DigitalTermData();
      }
      break;
    case "income-long-term-disability":
      data = LongTermDisabilityData();
      break;
    default:
      window.location = "/not-found";
  };

  return (
    <Container fluid className={product === "term" ? 'term-landing product-landing' : 'product-landing'}>
      <Helmet>
        <title>{`${data.name}`}</title>
      </Helmet>

    {(isTermInsurance && isTermVersion) ? 
      <Fragment>
        { data.screen }
      </Fragment> 
      :
      <Fragment>
      <HeroImage
        title={data.name}
        subtitle={data.subtitle}
        subtitleExtended={data.subtitleExtended}
        heroText={data.heroText}
        about={data.about}
        heroImageUrl={heroImageUrl}
        heroImageTabletUrl={heroImageTabletUrl}
        heroImageMobileUrl={heroImageMobileUrl}
        hasQuickQuoteDisclaimer={data.hasQuickQuoteDisclaimer}
        form={!isBusinessSolutions ? data.basicInfoForm() : null}
        isTermInsurance={isTermInsurance}
        isFinalExpense={isFinalExpense}
        isWholeLife={isWholeLife}
        isUniversalLife={isUniversalLife}
        isLoanProtection={isLoanProtection}
        isBusinessSolutions={isBusinessSolutions}
        isTestimonialPanelEnable={isTestimonialPanelEnable}
        />
      {(isTermInsurance || isFinalExpense || isWholeLife || isUniversalLife) && <Carriers category={data.productCarriers && data.productCarriers.productCategory} productCarriers={data.productCarriers && data.productCarriers.productCarrierData} carrierVerbiage = "We shop top-rated carriers to find you the best value" />} 
      {isTermInsurance && <TermAbout data={data.about}/>}
      {isFinalExpense && <FinalExpenseAbout data={data.about}/>}
      {isWholeLife && <WholeLifeAbout data={data.about}/>}
      {isUniversalLife && <UniversalLifeAbout data={data.about} carriers={data.productCarriers && data.productCarriers.productCarrierData} category={data.productCarriers && data.productCarriers.productCategory} handleSubmitClick={handleExternalSubmit} />}
      {isLoanProtection && <LoanProtectionAbout data={data.about} />}
      {isBusinessSolutions && <BusinessSolutionsAbout data={data.about} />}
      {(!isTermInsurance && !isFinalExpense && !isWholeLife && !isUniversalLife && !isLoanProtection && !isBusinessSolutions) && data.productSpecifications && !data.hideMarketing &&
        <ProductSpecifications data={data.productSpecifications} coverageAmount={data.specs.coverageAmount} />
      }

      {isBusinessSolutions && 
        <Fragment>
          <Products data={data.productData} journeys={data.availableProductJourneys} />
          <SolutionArticleSummary data={data.termArticle} scrollToElement={scrollToElement}/>
        </Fragment>
      }

      {(!isTermInsurance || !isFinalExpense || !isWholeLife || !isUniversalLife || !isLoanProtection || !isBusinessSolutions) && data.marketing && !data.hideMarketing && <Marketing data={data.marketing}/>}
      {isTermInsurance && <TermPurchasing data={data.purchasingProcess}/>}
      {isFinalExpense && <FinalExpensePurchasing data={data.purchasingProcess}/>}
      {isWholeLife && <WholeLifePurchasing data={data.purchasingProcess}/>}
      {isUniversalLife && <UniversalLifePurchasing data={data.purchasingProcess}/>}
      {isLoanProtection && <LoanProtectionPurchasing data={data.purchasingProcess} />}
      {isBusinessSolutions && <BusinessSolutionsPurchasing data={data.purchasingProcess} journeys={data.availableProductJourneys} />}
      {(!isTermInsurance && !isFinalExpense && !isWholeLife && !isUniversalLife && !isLoanProtection && !isBusinessSolutions) && data.purchasingProcess && !data.hideMarketing && <PurchasingProcess data={data.purchasingProcess}/>}
      {(isTermInsurance || isFinalExpense || isWholeLife || isUniversalLife || isLoanProtection) && <ProductPolicyCost data={data.policyCost} location={location} action={handleExternalSubmit} />}
      {(!isTermInsurance || !isFinalExpense || !isWholeLife || !isUniversalLife || !isLoanProtection) && data.articleKeys && !data.hideMarketing &&
        <Articles articleKeys={data.articleKeys} title={data.articleTitle} videoData={product === "term" ? data.productSpecifications.video : null} />
      }

      {isBusinessSolutions && 
      <Fragment>
        <Statistics data={data.statistics} />
        <SolutionArticleDetail data={data.termDetailArticle} journeys={data.availableProductJourneys} />
      </Fragment>
      }

      {data.faq && !data.hideMarketing &&
        <FAQs 
          data={data.faq} 
          isTermInsuranceProduct={isTermInsurance}
          isFinalExpenseProduct={isFinalExpense}
          isWholeLifeProduct={isWholeLife}
          isUniversalLifeProduct={isUniversalLife}
          isLoanProtectionProduct={isLoanProtection}
          isBusinessSolutions={isBusinessSolutions}
          isPermProduct={isPermProduct}
          isJuviProduct={isJuviProduct}
          isAddDefaultFaq={data.isAddDefaultFaq !== undefined ? data.isAddDefaultFaq : true} 
          hideCommonFaq={data.hideCommonFaq} />
      }
      {(isTermInsurance || isFinalExpense || isWholeLife || isUniversalLife || isLoanProtection) && <TermArticles data={data.termArticle}/>}

      {data.showRating && !data.hideMarketing &&
        <Reviews data={data.Review} />
      }

      {data.footerMessage && !data.hideMarketing && <BeforeFooter title={data.footerMessage} action={handleExternalSubmit} isModernized={(isTermInsurance || isFinalExpense || isWholeLife || isUniversalLife || isLoanProtection || isBusinessSolutions) ? true : false} buttonLabel={data.footerButtonName ? data.footerButtonName : "GET A QUOTE"} journeys={data.availableProductJourneys} />}
      {(isTermInsurance || isLoanProtection || isSupplemental || isPermProduct || isJuviProduct) && 
         data.saveMyQuotePartnerReferral
      }
      </Fragment>
    }
    </Container>

  );
};
